import React, { useState, useEffect } from 'react';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificacionesProvider } from './context/NotificacionesContext';
import { ThemeProvider } from './context/ThemeContext';
import Protected from './components/auth/Protected';
import Layout from './components/layout/Layout';
import AdminDashboard from './components/dashboard/AdminDashboard';
import AdminAdvancedDashboard from './components/admin/AdminAdvancedDashboard';
import Dashboard from './components/Dashboard';
import ClienteDashboardOptimizado from './components/dashboard/ClienteDashboardOptimizado';
import ClientesList from './components/clientes/ClientesList';
import EquiposList from './components/equipos/EquiposList';
import MisEquipos from './components/equipos/MisEquipos';
import OrdenesList from './components/ordenes/OrdenesList';
import MisOrdenes from './components/ordenes/MisOrdenes';
import PresupuestosList from './components/presupuestos/PresupuestosList';
import Agenda from './components/agenda/Agenda';
import AgendamientosInteligentes from './components/agenda/AgendamientosInteligentes';
import PlanificadorGlobal from './components/agenda/PlanificadorGlobal';
import MisAgendamientos from './components/agenda/MisAgendamientos';
import MisHoras from './components/horas/MisHoras';
import ControlHoras from './components/horas/ControlHoras';
import CalendarioVisitas from './components/calendario/CalendarioVisitas';
import Usuarios from './components/usuarios/Usuarios';
import Pendientes from './components/pendientes/Pendientes';
import TicketsSoporte from './components/tickets/TicketsSoporte';
import MonitorServicios from './components/clientes/MonitorServicios';
import RecepcionesList from './components/recepciones/RecepcionesList';
import EntregasDocumentosList from './components/documentos/EntregasDocumentosList';
import MovilidadList from './components/movilidad/MovilidadList';

function MainApp() {
  const { user } = useAuth();
  const [seccion, setSeccion] = useState(() => {
    // Verificar si la URL contiene un path de equipo
    const path = window.location.pathname;
    const equipoMatch = path.match(/\/equipos\/([a-f0-9]{24})/i);
    
    if (equipoMatch) {
      // Si hay un ID de equipo en la URL, ir a equipos
      return user?.rol === 'cliente' ? 'mis_equipos' : 'equipos';
    }
    
    // Establecer sección inicial según el rol
    return user?.rol === 'cliente' ? 'dashboard_cliente' : 'dashboard';
  });
  
  const [equipoIdFromUrl, setEquipoIdFromUrl] = useState(() => {
    // Extraer el ID del equipo de la URL si existe
    const path = window.location.pathname;
    const equipoMatch = path.match(/\/equipos\/([a-f0-9]{24})/i);
    return equipoMatch ? equipoMatch[1] : null;
  });
  
  // Estado para pasar un agendamiento al formulario de órdenes
  const [agendamientoParaOrden, setAgendamientoParaOrden] = useState(null);

  // Efecto para asegurar que los clientes siempre tengan una sección válida
  useEffect(() => {
    if (user?.rol === 'cliente' && !['dashboard_cliente', 'mis_ordenes', 'mis_equipos', 'mis_agendamientos', 'mis_horas', 'tickets', 'monitor_servicios'].includes(seccion)) {
      setSeccion('dashboard_cliente');
    }
  }, [user, seccion]);

  // Efecto para manejar navegación programática desde el dashboard
  useEffect(() => {
    const handleNavigate = (event) => {
      const detail = event.detail;
      if (typeof detail === 'object' && detail !== null && detail.seccion) {
        // Navegación con datos adicionales
        setSeccion(detail.seccion);
        if (detail.agendamientoParaOrden) {
          setAgendamientoParaOrden(detail.agendamientoParaOrden);
        }
      } else {
        // Navegación simple (string)
        setSeccion(detail);
      }
    };

    window.addEventListener('navigate', handleNavigate);
    return () => window.removeEventListener('navigate', handleNavigate);
  }, []);
  
  // Efecto para detectar cambios en la URL (para navegación directa)
  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname;
      const equipoMatch = path.match(/\/equipos\/([a-f0-9]{24})/i);
      
      if (equipoMatch) {
        setEquipoIdFromUrl(equipoMatch[1]);
        setSeccion(user?.rol === 'cliente' ? 'mis_equipos' : 'equipos');
      }
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [user]);

  let contenido = null;
  if (user?.rol === 'cliente') {
    if (seccion === 'dashboard_cliente') contenido = <ClienteDashboardOptimizado />;
    if (seccion === 'tickets') contenido = <TicketsSoporte />;
    if (seccion === 'monitor_servicios') contenido = <MonitorServicios />;
    if (seccion === 'mis_ordenes') contenido = <MisOrdenes />;
    if (seccion === 'mis_equipos') contenido = <MisEquipos equipoIdFromUrl={equipoIdFromUrl} onEquipoViewed={() => setEquipoIdFromUrl(null)} />;
    if (seccion === 'mis_agendamientos') contenido = <MisAgendamientos />;
    if (seccion === 'mis_horas') contenido = <MisHoras />;
  } else {
    if (seccion === 'dashboard') contenido = <AdminDashboard />;
    if (seccion === 'dashboard_ejecutivo') contenido = <Dashboard />;
    if (seccion === 'control_horas') contenido = <ControlHoras />;
    if (seccion === 'calendario_visitas') contenido = <CalendarioVisitas />;
    if (seccion === 'admin_avanzado') contenido = <AdminAdvancedDashboard />;
    if (seccion === 'tickets') contenido = <TicketsSoporte />;
    if (seccion === 'clientes') contenido = <ClientesList />;
    if (seccion === 'equipos') contenido = <EquiposList equipoIdFromUrl={equipoIdFromUrl} onEquipoViewed={() => setEquipoIdFromUrl(null)} />;
    if (seccion === 'ordenes') contenido = <OrdenesList agendamientoParaOrden={agendamientoParaOrden} onAgendamientoUsado={() => setAgendamientoParaOrden(null)} />;
    if (seccion === 'presupuestos') contenido = <PresupuestosList />;
    if (seccion === 'agenda') contenido = <Agenda />;
    if (seccion === 'agendamientos_inteligentes') contenido = <AgendamientosInteligentes />;
    if (seccion === 'planificador_global') contenido = <PlanificadorGlobal />;
    if (seccion === 'usuarios') contenido = <Usuarios />;
    if (seccion === 'pendientes') contenido = <Pendientes user={user} usuarios={[]} clientes={[]} />;
    if (seccion === 'recepciones_equipos') contenido = <RecepcionesList />;
    if (seccion === 'entregas_documentos') contenido = <EntregasDocumentosList />;
    if (seccion === 'movilidad') contenido = <MovilidadList />;
  }

  return <Layout seccion={seccion} setSeccion={setSeccion}>{contenido}</Layout>;
}

export default function App() {
  return (
    <AuthProvider>
      <NotificacionesProvider>
        <ThemeProvider>
          <Protected>
            <MainApp />
          </Protected>
          <Toaster position="top-right" richColors closeButton />
        </ThemeProvider>
      </NotificacionesProvider>
    </AuthProvider>
  );
}
